"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type ClimbingDisplayPreviewProps = {
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

/*
 * An indoor lead wall under a warm key light: textured slate panels with a
 * T-nut grid, plywood volumes, five colour-coded routes, and a climber mid
 * cross-through on the orange line (the focal point) with the rope running
 * down-left through a quickdraw. The crash mat along the bottom sits under
 * the participant pill, so nothing important lives there.
 *
 * Motion: one chalk puff off the reaching hand — CSS transform/opacity only,
 * off under reduced motion.
 */
const CLIMBING_KEYFRAMES = `
@keyframes climb-chalk {
  0%, 55% { transform: translate3d(0, 0, 0) scale(0.5); opacity: 0 }
  65% { opacity: 0.85 }
  100% { transform: translate3d(5px, -8px, 0) scale(1.35); opacity: 0 }
}
.climb-chalk { animation: climb-chalk 4.8s ease-out infinite; transform-box: fill-box; transform-origin: center }
@media (prefers-reduced-motion: reduce) {
  .climb-chalk { animation: none; opacity: 0.35 }
}
`;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `climb-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

const HOLD_COLORS = {
  orange: ["#ffc08f", "#ff7a2f", "#b93d08"],
  teal: ["#8ff5e8", "#1fc3b2", "#0b6e66"],
  pink: ["#ffaed3", "#ff4f9a", "#a3124f"],
  yellow: ["#fff3a8", "#ffd23f", "#b98d00"],
  purple: ["#cbb5ff", "#8a5cff", "#4a22b8"],
} as const;

type HoldColor = keyof typeof HOLD_COLORS;

const HOLD_SHAPES = {
  jug: "M-9 -2 C-9 -8 9 -8 9 -2 C9 4 4 7 0 7 C-4 7 -9 4 -9 -2 Z",
  crimp: "M-8 -1.6 Q0 -4.4 8 -1.6 L8 1.6 Q0 3.2 -8 1.6 Z",
  sloper: "M-10 3 C-10 -6 10 -6 10 3 Q0 6 -10 3 Z",
  pinch: "M-3 -9 C3 -9 4 -2 3 9 C1 10 -1 10 -3 9 C-4 -2 -5 -9 -3 -9 Z",
  blob: "M-7 -5 C-2 -10 8 -6 8 0 C8 6 2 8 -3 7 C-9 6 -11 -1 -7 -5 Z",
} as const;

type Hold = {
  x: number;
  y: number;
  shape: keyof typeof HOLD_SHAPES;
  color: HoldColor;
  rotate?: number;
  scale?: number;
  chalk?: boolean;
};

// Laid out around the two reserved zones: the label (top centre) and the
// participant pill (bottom centre, y > 160).
const HOLDS: Hold[] = [
  // Orange — the climber's line, bottom-left to top-right.
  { x: 38, y: 178, shape: "jug", color: "orange", scale: 1.15, chalk: true },
  { x: 84, y: 150, shape: "crimp", color: "orange", rotate: -12 },
  { x: 110, y: 114, shape: "blob", color: "orange", chalk: true },
  { x: 152, y: 88, shape: "jug", color: "orange", rotate: -20, chalk: true },
  { x: 155, y: 135, shape: "crimp", color: "orange", rotate: 8 },
  { x: 197, y: 151, shape: "sloper", color: "orange", scale: 0.9 },
  { x: 202, y: 50, shape: "jug", color: "orange", rotate: 15, chalk: true },
  { x: 240, y: 40, shape: "blob", color: "orange", scale: 1.1 },
  // Teal
  { x: 250, y: 86, shape: "pinch", color: "teal", rotate: 18 },
  { x: 282, y: 104, shape: "blob", color: "teal", scale: 1.1 },
  { x: 224, y: 104, shape: "crimp", color: "teal", rotate: -6 },
  { x: 132, y: 58, shape: "pinch", color: "teal", rotate: -24, scale: 0.9 },
  // Pink
  { x: 58, y: 60, shape: "sloper", color: "pink", rotate: 8 },
  { x: 108, y: 92, shape: "crimp", color: "pink", rotate: 20 },
  { x: 268, y: 148, shape: "jug", color: "pink", rotate: -10 },
  // Yellow
  { x: 22, y: 102, shape: "jug", color: "yellow", rotate: -14 },
  { x: 92, y: 46, shape: "blob", color: "yellow", scale: 0.85 },
  { x: 218, y: 130, shape: "sloper", color: "yellow", scale: 0.8 },
  // Purple
  { x: 30, y: 148, shape: "pinch", color: "purple", rotate: -30 },
  { x: 270, y: 194, shape: "blob", color: "purple", scale: 1.2 },
  { x: 72, y: 110, shape: "crimp", color: "purple", rotate: 30 },
];

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        {(Object.keys(HOLD_COLORS) as HoldColor[]).map((color) => {
          const [light, base, dark] = HOLD_COLORS[color];
          return (
            <linearGradient
              key={color}
              id={id(`hold-${color}`)}
              x1="0"
              y1="0"
              x2="1"
              y2="1"
            >
              <stop offset="0" stopColor={light} />
              <stop offset="0.45" stopColor={base} />
              <stop offset="1" stopColor={dark} />
            </linearGradient>
          );
        })}
        <radialGradient id={id("chalk")}>
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}

function HoldShape({ hold, id }: { hold: Hold; id: IdFn }) {
  const d = HOLD_SHAPES[hold.shape];
  return (
    <g
      transform={`translate(${hold.x} ${hold.y}) rotate(${hold.rotate ?? 0}) scale(${hold.scale ?? 1})`}
    >
      {/* Cast shadow down-right, away from the key light */}
      <path d={d} transform="translate(2 3)" fill="#06101a" opacity="0.45" />
      <path d={d} fill={`url(#${id(`hold-${hold.color}`)})`} />
      <ellipse
        cx="-3"
        cy="-3"
        rx="3.6"
        ry="1.6"
        transform="rotate(-15 -3 -3)"
        fill="#ffffff"
        opacity="0.55"
      />
      {hold.chalk ? (
        <ellipse cx="1" cy="-1" rx="4" ry="2" fill="#ffffff" opacity="0.3" />
      ) : null}
      <circle cx="0.5" cy="0.5" r="1.2" fill="#14181f" opacity="0.7" />
    </g>
  );
}

/** Plywood volumes, lit from the top-left. */
function Volumes() {
  const lit = "#f0d6aa";
  const mid = "#cfa56d";
  const shade = "#93683a";
  const deep = "#6d4a26";
  return (
    <g>
      {/* Big pyramid, left */}
      <polygon points="14,40 104,58 62,80" fill={lit} />
      <polygon points="14,40 62,80 36,132" fill={mid} />
      <polygon points="104,58 36,132 62,80" fill={shade} />
      <path
        d="M14 40 L62 80 L104 58 M62 80 L36 132"
        stroke="#fff4dc"
        strokeOpacity="0.55"
        strokeWidth="0.8"
        fill="none"
      />
      <path
        d="M24 48 L90 60 M30 56 L82 66 M22 64 L48 96"
        stroke="#8a6232"
        strokeOpacity="0.18"
        strokeWidth="0.7"
        fill="none"
      />
      {/* Dual-tex hex, right */}
      <polygon points="236,150 266,112 270,146" fill={lit} />
      <polygon points="266,112 298,128 270,146" fill={mid} />
      <polygon points="298,128 292,176 270,146" fill={shade} />
      <polygon points="292,176 236,150 270,146" fill={deep} />
      <path
        d="M236 150 L270 146 L266 112 M270 146 L298 128"
        stroke="#fff4dc"
        strokeOpacity="0.5"
        strokeWidth="0.8"
        fill="none"
      />
      {/* Small wedge, top-right */}
      <polygon points="226,14 292,4 258,30" fill={lit} />
      <polygon points="226,14 258,30 266,58" fill={mid} />
      <polygon points="292,4 266,58 258,30" fill={shade} />
    </g>
  );
}

const SKIN = "#e3a57f";
const SKIN_SHADE = "#b9775a";

/** Seen from behind, mid cross-through: right hand reaching the next jug. */
function Climber({ shadow = false }: { shadow?: boolean }) {
  const c = (color: string) => (shadow ? "#06101a" : color);
  return (
    <g
      opacity={shadow ? 0.3 : 1}
      transform={shadow ? "translate(8 6)" : undefined}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Legs */}
      <path
        d="M182 113 L192 132 L196 148"
        stroke={c("#2b2f3b")}
        strokeWidth="6.5"
        fill="none"
      />
      <path
        d="M173 113 L161 119 L155 132"
        stroke={c("#23262f")}
        strokeWidth="6.5"
        fill="none"
      />
      {/* Shoes */}
      <path
        d="M193 147 L200 147 Q202 152 197 153 L192 152 Z"
        fill={c("#1d1b2c")}
      />
      <path
        d="M151 131 L158 131 Q160 136 155 137 L150 136 Z"
        fill={c("#1d1b2c")}
      />
      {!shadow ? (
        <>
          <path
            d="M197 147.5 L200 147.5 Q201.6 150 200 151.6"
            stroke="#ff7a2f"
            strokeWidth="1.4"
            fill="none"
          />
          <path
            d="M155 131.5 L158 131.5 Q159.6 134 158 135.6"
            stroke="#ff7a2f"
            strokeWidth="1.4"
            fill="none"
          />
        </>
      ) : null}
      {/* Arms */}
      <path
        d="M186 86 L194 69 L200 54"
        stroke={c(SKIN)}
        strokeWidth="4.8"
        fill="none"
      />
      <path
        d="M169 88 L159 96 L152 90"
        stroke={c(SKIN_SHADE)}
        strokeWidth="4.8"
        fill="none"
      />
      <circle cx="200.5" cy="52" r="3" fill={c(SKIN)} />
      <circle cx="151.5" cy="88.5" r="2.8" fill={c(SKIN_SHADE)} />
      {/* Torso — coral tee */}
      <path
        d="M166 87 Q177 79 190 85 L185 113 Q178 116 171 113 Z"
        fill={c("#ff6b4a")}
      />
      {!shadow ? (
        <path
          d="M181 84 Q187 85 190 85 L185 113 Q182 114 179 114 Z"
          fill="#c9442b"
          opacity="0.7"
        />
      ) : null}
      {/* Neck + head (back of head, hair in a bun) */}
      <rect x="175.5" y="77" width="5" height="6" rx="2" fill={c(SKIN_SHADE)} />
      <circle cx="178" cy="73" r="6.6" fill={c("#2b1d16")} />
      <circle cx="178.5" cy="66" r="3" fill={c("#2b1d16")} />
      {/* Harness + chalk bag */}
      <path
        d="M170 112 Q178 117 186 112"
        stroke={c("#121318")}
        strokeWidth="3.2"
        fill="none"
      />
      <rect
        x="174.5"
        y="113"
        width="7"
        height="7.5"
        rx="2"
        fill={c("#ffd23f")}
      />
      {!shadow ? (
        <>
          <rect x="174.5" y="113" width="7" height="2" rx="1" fill="#2a2a2a" />
          {/* Racked quickdraws on the gear loop */}
          <path
            d="M170 114 L169 120 M187 114 L188.5 120"
            stroke="#8a5cff"
            strokeWidth="1.6"
          />
          {/* Rim light from the key light, top-left */}
          <path
            d="M167 87 Q177 80.5 189 85"
            stroke="#ffe7cf"
            strokeOpacity="0.8"
            strokeWidth="1"
            fill="none"
          />
          <path
            d="M185 84 L192.5 68 L198.5 53"
            stroke="#ffe7cf"
            strokeOpacity="0.7"
            strokeWidth="1"
            fill="none"
          />
          <path
            d="M172.4 69 Q175 66.4 178 66.6"
            stroke="#a07a62"
            strokeOpacity="0.8"
            strokeWidth="1"
            fill="none"
          />
        </>
      ) : null}
    </g>
  );
}

function Rope() {
  const d = "M178 116 C170 128 152 134 126 139 C102 144 86 174 62 224";
  return (
    <g fill="none" strokeLinecap="round">
      <path
        d={d}
        stroke="#06101a"
        strokeOpacity="0.4"
        strokeWidth="3.6"
        transform="translate(3 3)"
      />
      <path d={d} stroke="#b9f21f" strokeWidth="2.2" />
      <path
        d={d}
        stroke="#f2ffc4"
        strokeOpacity="0.75"
        strokeWidth="0.8"
        strokeDasharray="2.5 3.5"
      />
      {/* Quickdraw the rope runs through */}
      <rect x="121.5" y="126" width="3" height="9" rx="1.2" fill="#2a2d36" />
      <ellipse
        cx="123"
        cy="125"
        rx="2.4"
        ry="3"
        stroke="#d9dde6"
        strokeWidth="1.2"
      />
      <ellipse
        cx="123.5"
        cy="138"
        rx="2.8"
        ry="3.4"
        stroke="#d9dde6"
        strokeWidth="1.3"
      />
      <circle cx="123" cy="122" r="1.5" fill="#9aa3b2" />
    </g>
  );
}

function Wall({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-0 left-1/2 -translate-x-1/2"
      width="300"
      height="220"
      viewBox="0 0 300 220"
      aria-hidden="true"
      focusable="false"
    >
      <Volumes />
      {HOLDS.map((hold) => (
        <HoldShape
          key={`${hold.color}-${hold.x}-${hold.y}`}
          hold={hold}
          id={id}
        />
      ))}
      <Climber shadow />
      <Rope />
      <Climber />
      <circle
        className="climb-chalk"
        cx="206"
        cy="47"
        r="7"
        fill={`url(#${id("chalk")})`}
      />
    </svg>
  );
}

export function ClimbingDisplayPreview({
  participants,
}: ClimbingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Climbing"
      backgroundClassName="bg-[#2e4459]"
      participants={participants}
      scene={
        <>
          <style href="climbing-scene-keyframes" precedence="default">
            {CLIMBING_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Wall: slate panels, warm key light top-left, cool fill right */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(90% 85% at 32% 12%, rgba(255,210,160,0.42) 0%, rgba(255,210,160,0.12) 45%, transparent 70%), radial-gradient(70% 70% at 92% 55%, rgba(110,190,255,0.16) 0%, transparent 70%), linear-gradient(180deg, #34506a 0%, #3c5b76 45%, #2a3f55 100%)",
            }}
          />
          {/* Panel seams */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, transparent 0 74px, rgba(8,16,26,0.45) 74px 75px, rgba(255,255,255,0.07) 75px 76px), repeating-linear-gradient(180deg, transparent 0 72px, rgba(8,16,26,0.4) 72px 73px, rgba(255,255,255,0.06) 73px 74px)",
            }}
          />
          {/* T-nut grid */}
          <div
            className="absolute inset-0 opacity-70"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(10,18,28,0.6) 0 1px, transparent 1.6px)",
              backgroundSize: "14px 14px",
            }}
          />

          <Wall id={id} />

          {/* Crash mat */}
          <div className="absolute inset-x-0 bottom-0 h-[24px] bg-[linear-gradient(180deg,#2c447a_0%,#18264a_100%)] shadow-[0_-6px_14px_rgba(6,12,24,0.45)]">
            <div className="absolute inset-x-0 top-0 h-px bg-white/25" />
            <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent_0_58px,rgba(0,0,0,0.35)_58px_60px)]" />
          </div>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_120%_120%_at_40%_30%,transparent_50%,rgba(8,14,24,0.55)_100%)]" />
      }
    />
  );
}
